import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Breadcrumbs from '../components/Breadcrumbs';
import { FaPhone, FaMapMarkerAlt, FaHome } from 'react-icons/fa';
import CasaEncuentroCarousel from '../components/CasaEncuentroCarousel';
import api from '../services/api';
import './CasaEncuentroDetalle.css';

/** Divide la descripción en párrafos (saltos de línea del editor). */
function parrafosDescripcion(texto) {
  if (!texto || !String(texto).trim()) return [];
  return String(texto)
    .split(/\r?\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function youtubeEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  const match = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

const CasaEncuentroDetalle = () => {
  const { slug } = useParams();

  const { data: casa, isLoading, error } = useQuery({
    queryKey: ['casa-encuentro', slug],
    queryFn: async () => {
      const res = await api.get(`/casas-encuentro/slug/${slug}`);
      return res.data;
    },
    enabled: !!slug,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="casa-encuentro-page">
        <Breadcrumbs />
        <div className="container"><p className="loading">Cargando...</p></div>
      </div>
    );
  }
  if (error || !casa) {
    return (
      <div className="casa-encuentro-page">
        <Breadcrumbs />
        <div className="container">
          <section className="section">
            <h1 className="page-title">Casa no encontrada</h1>
            <p>No existe una casa de encuentro con esta dirección, o no está disponible en este momento.</p>
            <Link to="/" className="casa-encuentro-back-link">
              <FaHome /> Volver al inicio
            </Link>
          </section>
        </div>
      </div>
    );
  }

  const imagenes = (casa.imagenes || [])
    .map((i) => (typeof i === 'string' ? i : i.imagen_url))
    .filter(Boolean);
  const embedUrl = youtubeEmbedUrl(casa.video_url);
  const placeholder = '/images/placeholder-casa.svg';
  const parrafos = parrafosDescripcion(casa.descripcion);

  return (
    <div className="casa-encuentro-page">
      <Breadcrumbs />
      <section className="section casa-encuentro-hero">
        <div className="container">
          <h1 className="page-title">{casa.titulo}</h1>
        </div>
      </section>

      <section className="section casa-encuentro-content">
        <div className="container">
          <div className="casa-encuentro-grid">
            <div className="casa-encuentro-imagenes">
              <CasaEncuentroCarousel
                urls={imagenes}
                alt={casa.titulo}
                placeholder={placeholder}
              />
            </div>
            <div className="casa-encuentro-info">
              {parrafos.length > 0 && (
                <div className="casa-encuentro-texto">
                  {parrafos.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              )}
              {embedUrl && (
                <div className="casa-encuentro-video">
                  <iframe title={`Video ${casa.titulo}`} src={embedUrl} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
              {casa.telefono && (
                <p className="casa-encuentro-contacto">
                  <FaPhone className="icon-phone-upright" aria-hidden />
                  <a href={`tel:${casa.telefono.replace(/\D/g, '')}`}>{casa.telefono}</a>
                </p>
              )}
              {casa.direccion && (
                <p className="casa-encuentro-contacto">
                  <FaMapMarkerAlt aria-hidden />
                  <span>{casa.direccion}</span>
                </p>
              )}
              {!casa.telefono && !casa.direccion && (
                <p className="casa-encuentro-sin-datos">Teléfono y dirección disponibles próximamente.</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CasaEncuentroDetalle;
